/**
 * Utilidad para validación de formato de contraseña según política SDI
 * 
 * @description Valida que la contraseña cumpla con los requisitos de seguridad:
 *              - Mínimo 8 caracteres
 *              - Al menos un dígito
 *              - Al menos una letra minúscula
 *              - Al menos una letra mayúscula
 *              - Al menos un carácter especial
 *              - Máximo 20 caracteres
 *              - Sin espacios
 * 
 * @param {String} password - Contraseña a validar
 * @returns {Object} - { isValid: boolean, errors: Array<string> }
 * 
 * @example
 * const { isValid, errors } = validatePasswordFormat('MiPass123!')
 * if (!isValid) {
 *   console.log('Errores:', errors)
 * }
 */
export function validatePasswordFormat (password) {
  const errors = []

  if (!password) {
    return { isValid: false, errors: ['La contraseña es requerida'] }
  }

  if (password.length < 8) {
    errors.push('La contraseña debe tener mínimo 8 caracteres')
  }

  if (!/\d/.test(password)) {
    errors.push('La contraseña debe contener al menos un número')
  }

  if (!/[a-z]/.test(password)) {
    errors.push('La contraseña debe contener al menos una letra minúscula')
  }

  if (!/[A-Z]/.test(password)) {
    errors.push('La contraseña debe contener al menos una letra mayúscula')
  }

  if (!/[^a-zA-Z0-9]/.test(password)) {
    errors.push('La contraseña debe contener al menos un carácter especial')
  }

  if (/\s/.test(password)) {
    errors.push('La contraseña no puede contener espacios')
  }

  if (password.length > 20) {
    errors.push('La contraseña no puede tener más de 20 caracteres')
  }

  return {
    isValid: errors.length === 0,
    errors: errors
  }
}

/**
 * Regex para validación de contraseña según política SDI
 * 
 * @description Regex completo que valida todos los requisitos en una sola expresión
 * 
 * @type {RegExp}
 */
export const PASSWORD_REGEX = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,20}$/

/**
 * Valida el tipo de error de contraseña basado en el mensaje del backend
 * 
 * @description Identifica el tipo de error de contraseña a partir del mensaje de respuesta del backend.
 *              Retorna un string que identifica el tipo de error para su manejo posterior.
 * 
 * @param {String} mensaje - Mensaje de error del backend
 * @returns {String|null} - Tipo de error: 'INVALID_CURRENT', 'SAME_PASSWORD', 'INVALID_FORMAT', o null si no es un error de contraseña
 * 
 * @example
 * const errorType = validatePasswordError('La contraseña actual ingresada no es correcta')
 * // Retorna: 'INVALID_CURRENT'
 */
export function validatePasswordError (mensaje) {
  if (!mensaje || typeof mensaje !== 'string') {
    return null
  }

  const mensajeLower = mensaje.toLowerCase()

  // Error: Contraseña actual incorrecta
  if (mensajeLower.includes('contraseña actual') || 
      mensajeLower.includes('actual ingresada no es correcta') || 
      mensajeLower.includes('actual incorrecta')) {
    return 'INVALID_CURRENT'
  }

  // Error: Nueva contraseña igual a la actual
  if (mensajeLower.includes('igual a la contraseña actual') || 
      mensajeLower.includes('no puede ser igual') || 
      mensajeLower.includes('distinta')) {
    return 'SAME_PASSWORD'
  }

  // Error: Formato inválido
  if (mensajeLower.includes('formato inválido') || 
      mensajeLower.includes('formato') || 
      mensajeLower.includes('mínimo 8 caracteres') || 
      mensajeLower.includes('letra mayúscula')) {
    return 'INVALID_FORMAT'
  }

  return null
}




// WEBPACK FOOTER //
// ./src/utils/passwordValidation.js